import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  TextField,
  InputAdornment,
  Box,
  Button,
  Menu,
  MenuItem,
  useMediaQuery,
} from "@mui/material";
import { Search, ExpandMore, AccountCircle, Person, Logout } from "@mui/icons-material";
import { DateRange } from "react-date-range";
import { Popover } from "@mui/material";
import { format } from "date-fns";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

const TopBar = ({
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  dateRange,
  setDateRange,
  user,
  onLogout,
  onFilterChange,
  currentView,
  setCurrentView,
}) => {
  const [statusAnchor, setStatusAnchor] = useState(null);
  const [adminAnchor, setAdminAnchor] = useState(null);
  const [dateAnchor, setDateAnchor] = useState(null);

  const isMobile = useMediaQuery("(max-width:768px)");

  const openStatusMenu = (e) => setStatusAnchor(e.currentTarget);
  const closeStatusMenu = () => setStatusAnchor(null);

  const openAdminMenu = (e) => setAdminAnchor(e.currentTarget);
  const closeAdminMenu = () => setAdminAnchor(null);

  const chooseStatus = (s) => {
    setStatusFilter(s);
    closeStatusMenu();
    onFilterChange && onFilterChange({ status: s });
  };

  const showFilters = currentView === "KYC Logs";

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        bgcolor: "transparent",
        color: "inherit",
        mb: 3,
      }}
    >
      <Toolbar
        sx={{
          justifyContent: "space-between",
          px: 0,
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 2 : 0,
          alignItems: isMobile ? "flex-start" : "center",
        }}
      >
        {/* Search Field (visible only in Logs) */}
        {showFilters ? (
          <TextField
            size="small"
            placeholder="Search by User ID, PAN, Mobile..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              width: isMobile ? "100%" : 400,
              bgcolor: "white",
              borderRadius: 1,
            }}
          />
        ) : (
          <Box sx={{ width: isMobile ? "100%" : "auto" }} />
        )}

        {/* Right Side */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 2,
            width: isMobile ? "100%" : "auto",
            alignItems: isMobile ? "stretch" : "center",
          }}
        >
          {/* Filters only on Logs */}
          {showFilters && (
            <>
              {/* Status Filter */}
              <Button
                variant="outlined"
                endIcon={<ExpandMore />}
                sx={{ textTransform: "none", width: isMobile ? "100%" : "auto" }}
                onClick={openStatusMenu}
              >
                {statusFilter}
              </Button>

              <Menu anchorEl={statusAnchor} open={Boolean(statusAnchor)} onClose={closeStatusMenu}>
                <MenuItem onClick={() => chooseStatus("All Status")}>All Status</MenuItem>
                <MenuItem onClick={() => chooseStatus("Pending")}>Pending</MenuItem>
                <MenuItem onClick={() => chooseStatus("Approved")}>Approved</MenuItem>
                <MenuItem onClick={() => chooseStatus("Rejected")}>Rejected</MenuItem>
              </Menu>

              {/* Date Range Filter */}
              <Button
                variant="outlined"
                sx={{
                  textTransform: "none",
                  px: 2,
                  width: isMobile ? "100%" : "auto",
                  display: "flex",
                  justifyContent: isMobile ? "center" : "space-between", 
                  alignItems: "center",
                  gap: isMobile ? 1 : 0,
                }}
                onClick={(e) => setDateAnchor(e.currentTarget)}
              >
                <span>
                  {dateRange?.[0]?.startDate && dateRange?.[0]?.endDate
                    ? `${format(dateRange[0].startDate, "dd/MM/yyyy")} - ${format(
                        dateRange[0].endDate,
                        "dd/MM/yyyy"
                      )}`
                    : "Select Date Range"}
                </span>

                {dateRange?.[0]?.startDate && (
                  <Box
                    component="span"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDateRange([{ startDate: null, endDate: null, key: "selection" }]);
                      onFilterChange &&
                        onFilterChange({ dateRange: [{ startDate: null, endDate: null }] });
                    }}
                    sx={{
                      cursor: "pointer",
                      fontWeight: "bold",
                      ml: isMobile ? 0 : 1, // no left margin in mobile
                    }}
                  >
                    ✕
                  </Box>
                )}
              </Button>

              <Popover
                open={Boolean(dateAnchor)}
                anchorEl={dateAnchor}
                onClose={() => setDateAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              >
                <DateRange
                  editableDateInputs={true}
                  moveRangeOnFirstSelection={false}
                  ranges={
                    dateRange?.[0]?.startDate
                      ? dateRange
                      : [
                          {
                            startDate: new Date(),
                            endDate: new Date(),
                            key: "selection",
                          },
                        ]
                  }
                  onChange={(item) => {
                    setDateRange([item.selection]);
                    onFilterChange && onFilterChange({ dateRange: item.selection });
                  }}
                />
              </Popover>
            </>
          )}

          {/* Profile Button - Always Visible */}
          <Button
            variant="contained"
            startIcon={<AccountCircle />}
            endIcon={<ExpandMore />}
            sx={{
              textTransform: "none",
              borderRadius: "20px",
              width: isMobile ? "100%" : "auto",
            }}
            onClick={openAdminMenu}
          >
            {user?.username || "Admin"}
          </Button>

          <Menu
            anchorEl={adminAnchor}
            open={Boolean(adminAnchor)}
            onClose={closeAdminMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem
              onClick={() => {
                closeAdminMenu();
                setCurrentView("Profile");
                sessionStorage.setItem("currentView", "Profile");
              }}
            >
              <Person fontSize="small" sx={{ mr: 1 }} />
              My Profile
            </MenuItem>
            <MenuItem
              onClick={() => {
                closeAdminMenu();
                onLogout && onLogout();
              }}
            >
              <Logout fontSize="small" sx={{ mr: 1 }} />
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
